import React, { ReactElement, ReactNode } from 'react'
import { Divider, Wrapper, P } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { screenMax, screenMin } from '@farewill/ui/helpers/responsive'
import { CLOUDINARY_ROOT_PATH } from '@farewill/ui/components/Image/constants'

import styled from 'styled-components'
import { CHARITY_AMOUNT_RAISED } from 'config'
import DefaultLayout from 'layouts/DefaultLayout'
import { PRODUCTS } from 'lib/products/constants'
import { SHARE_IMAGE_DIMENSIONS } from 'lib/sharing/constants'

import RoleHero from './Hero'
import Values from '../Values'
import RoleSidebar from './RoleSidebar'
import RoleNextStepsBox from './Box'

/**
 * Used as a heading for a section
 * @example <RoleTitle>The role</RoleTitle>
 */
export const RoleTitle = styled('h4')`
  line-height: 1.33;
  font-weight: ${FONT.WEIGHT.BOLD};
  margin-bottom: ${GTR.S};
  font-size: ${FONT.SIZE.M};
`

/**
 * Used to separate the sections of a role
 * @example <RoleSection><RoleTitle>The role</RoleTitle> ... </RoleSection>
 */
export const RoleSection = styled(Wrapper)`
  margin-bottom: ${GTR.L};

  & > p {
    margin-bottom: 12px;
  }

  overflow: wrap;
`
/**
 * Used to create a bulleted list that's Farewill branded
 * @example <RoleTitle>Our Values</RoleTitle> <RoleUl><li>Customer First</li><li>Act Intrapenurail</li></RoleUl>
 */
export const RoleUl = styled('ul')`
  & > li {
    margin-bottom: ${GTR.XS};
    padding-left: ${GTR.XS};
  }

  & > li::marker {
    color: ${COLOR.ACCENT.PRIMARY};

    content: '•';
  }
`

const StyledLeadingP = styled(P)`
  font-size: ${FONT.SIZE.L};
`
const ParentContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  column-gap: 100px;
  row-gap: ${GTR.XL};
  max-width: 1024px;

  margin: 0 auto;
`

const BodyContainer = styled('article')`
  flex: 2;
  margin-bottom: 0;
  min-width: 270px;

  ${screenMin.s`
    max-width: 576px;
  `};
`

const StyledFixedSidebarContainer = styled('aside')`
  gap: ${GTR.M};
  min-width: 220px;
  max-width: fit-content;
  align-self: flex-start;
  flex: 1;

  ${screenMin.m`
    position: sticky;
    top: ${GTR.M};
  `};
`

const StyledDivider = styled(Divider)`
  ${screenMax.s`
    display: none;
  `};
`

/** This should be copied and pasted for now and refactored later */
export const RoleTemplate = ({
  roleName,
  roleUrl,
  location,
  salary,
  contractType,
  closingDate,
  content,
  product = PRODUCTS.WILLS,
}: {
  roleName: string
  roleUrl: string
  location: string
  salary: string
  contractType: string
  closingDate: string
  content: ReactNode
  product?: PRODUCTS
}): ReactElement => (
  <DefaultLayout
    title={`Work with Us | ${roleName}`}
    description="Join us in changing how the world thinks and plans for death."
    headerColor={COLOR.ACCENT.PRIMARY}
    shareImage={`${CLOUDINARY_ROOT_PATH}/c_fill,q_auto,w_${SHARE_IMAGE_DIMENSIONS.WIDTH},h_${SHARE_IMAGE_DIMENSIONS.HEIGHT}/photos/photo-meeting-on-sofa.jpg`}
  >
    <Wrapper background={COLOR.WHITE}>
      <RoleHero roleName={roleName} roleUrl={roleUrl} />
      <Wrapper container>
        <ParentContainer>
          <StyledFixedSidebarContainer>
            <RoleSidebar
              roleUrl={roleUrl}
              location={location}
              salary={salary}
              contractType={contractType}
              closingDate={closingDate}
            />
          </StyledFixedSidebarContainer>
          <BodyContainer>
            {/* This section is the same for all roles */}
            <RoleSection>
              <StyledLeadingP>
                All of us are going to die. It’s a fundamental, tragic part of
                what it is to be human. But the services that should be
                supporting us haven’t changed in decades.
              </StyledLeadingP>
              <P>
                From writing your will to planning a funeral or organising
                probate, the way we deal with death can be complicated and
                expensive. Instead of feeling supported, people feel lost.
              </P>
              {product === PRODUCTS.PROBATE ? (
                <P>
                  But it doesn’t need to be this way. We started Farewill to
                  make the experience warmer, brighter and friendlier. Now we’re
                  the UK’s top rated probate provider, and help our customers
                  pledge over £300 million a year to charities through gifts
                  left in their wills.
                </P>
              ) : (
                <>
                  <P>
                    But it doesn’t need to be this way. We started Farewill to
                    make the experience warmer, brighter and friendlier.
                  </P>
                  <P>
                    We’re the UK’s biggest will writer and one of the leading
                    funeral companies in the country. Our generous customers
                    have pledged {CHARITY_AMOUNT_RAISED} to charities, through
                    gifts left in their wills.
                  </P>
                </>
              )}
              <P>
                We’ve also joined the Dignity Group - one of the major providers
                of funeral services in the UK. Together, we’re on a mission to
                change the way the world deals with death. It’s no small task -
                and we’re looking for a {roleName} to help us do it.
              </P>
            </RoleSection>
            {content}
            <RoleNextStepsBox roleUrl={roleUrl} />
          </BodyContainer>
        </ParentContainer>
        <StyledDivider
          size="2px"
          margin={[GTR.XXL, 0, 0, 0]}
          marginFromL={[128, 0, 0, 0]}
        />
        <Values />
      </Wrapper>
    </Wrapper>
  </DefaultLayout>
)
